import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  Search,
  Bell,
  Plus,
  Command,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Dropdown } from '@/components/ui/Dropdown';
import { useAuthStore } from '@/stores/authStore';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  showSearch?: boolean;
}

export function Header({ title, subtitle, actions, showSearch = true }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const userMenuItems = [
    {
      id: 'profile',
      label: 'Profile Settings',
      onClick: () => navigate('/settings/profile'),
    },
    {
      id: 'billing',
      label: 'Billing',
      onClick: () => navigate('/settings/billing'),
    },
    {
      id: 'divider',
      label: '---',
      disabled: true,
    },
    {
      id: 'logout',
      label: 'Sign Out',
      onClick: logout,
      danger: true,
    },
  ];

  return (
    <header className="h-16 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 px-6 flex items-center justify-between">
      {/* Left: Title or Search */}
      <div className="flex items-center gap-4 flex-1">
        {title ? (
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            )}
          </div>
        ) : showSearch ? (
          <form onSubmit={handleSearch} className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-12 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-xs text-gray-400">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>
          </form>
        ) : null}
      </div>

      {/* Right: Actions & User */}
      <div className="flex items-center gap-3">
        {actions}

        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/projects/new')}
        >
          New Project
        </Button>

        {/* Notifications */}
        <button
          className="relative p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-dark-700 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        </button>

        {/* User Menu */}
        <Dropdown
          trigger={
            <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
              <Avatar name={user?.displayName || user?.email} size="sm" />
            </button>
          }
          items={userMenuItems}
          align="right"
        />
      </div>
    </header>
  );
}

export function ProjectHeader({
  projectName,
  projectStatus,
  actions,
}: {
  projectName: string;
  projectStatus?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="h-14 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 px-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
          {projectName}
        </h1>
        {projectStatus && (
          <span
            className={cn(
              'px-2 py-0.5 text-xs font-medium rounded-full',
              projectStatus === 'Running'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : projectStatus === 'Stopped'
                ? 'bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-gray-400'
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            )}
          >
            {projectStatus}
          </span>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}
